// An ambiguous question's clarification (v3 `normalizeAmbiguousQuery`, ORP-19803): the answer asks
// "which one do you mean?" and offers chips. A chip goes back as the next turn — its `query` as
// the message, and its option id (or filter) as `selected_filter`, so the backend runs the reading
// the user picked instead of asking again.

import type {
  Clarification,
  ClarificationOption,
  SelectedFilter,
} from '../models/orpheusClarification.types'

type Raw = Record<string, unknown>

function isObject(value: unknown): value is Raw {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function str(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return ''
}

function toOption(option: unknown): ClarificationOption | null {
  if (typeof option === 'string') {
    const label = option.trim()
    return label ? { label, description: '', message: label } : null
  }
  if (!isObject(option)) return null

  const label = str(option.label, option.title, option.name, option.text, option.query)
  const description = str(option.description, option.desc, option.subtitle, option.explanation)
  const message = str(option.query, option.message, option.prompt) || label
  if (!label || !message) return null
  return {
    label,
    description,
    message,
    ...(Object.hasOwn(option, 'id') ? { id: option.id } : {}),
    ...(typeof option.dimension === 'string' && option.dimension
      ? { dimension: option.dimension }
      : {}),
    ...(Object.hasOwn(option, 'value') ? { value: option.value } : {}),
    ...(isObject(option.filter) ? { filter: option.filter } : {}),
  }
}

/**
 * The clarification an answer carries, or null. Read from the response body (live) or the stored
 * message (history), with the metadata's `decision.clarify` flag as the fallback signal.
 */
export function normalizeClarification(response: unknown, metadata: unknown): Clarification | null {
  if (!isObject(response)) return null
  const meta = isObject(metadata) ? metadata : {}
  const decisionMeta = isObject(meta.decision) ? meta.decision : {}

  const nestedRaw = response.ambiguous_query ?? response.ambiguousQuery ?? response.clarification
  const nested: unknown = nestedRaw ?? response
  const nestedRecord = isObject(nested) ? nested : {}
  const decision = nestedRecord.decision ?? response.decision ?? decisionMeta.clarify
  const isDecision = decision === true || decision === 'true'
  const type = typeof response.type === 'string' ? response.type.trim().toLowerCase() : ''
  const isClarify = type === 'clarify' || type === 'clarification' || decisionMeta.clarify === true
  if (!isDecision && !isClarify) return null

  const firstList = [
    Array.isArray(nested) ? nested : null,
    nestedRecord.options,
    nestedRecord.choices,
    nestedRecord.questions,
    nestedRecord.suggestions,
    response.options,
    response.choices,
  ].find(Array.isArray)
  const options = (firstList ?? [])
    .map(toOption)
    .filter((option): option is ClarificationOption => option !== null)
  if (!options.length) return null

  return {
    title: str(nestedRecord.title, response.title, isClarify ? response.content : ''),
    description: str(
      nestedRecord.description,
      isClarify ? response.description : nestedRecord.content,
    ),
    options,
  }
}

/** What a picked chip sends along with its message. */
export function clarificationFilter(option: ClarificationOption): SelectedFilter | undefined {
  if (option.id !== undefined && option.id !== null) return { option_id: option.id }
  return option.filter
}
